import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LinkIcon from '@mui/icons-material/Link';
import StorefrontIcon from '@mui/icons-material/Storefront';
import authInstance from '../../services/httpClient';

type LinkType = 'category' | 'product' | 'brand' | 'brands-page' | 'custom' | 'store';

interface LinkOption {
  id: string;
  name: string;
  imageUrl?: string;
}

interface LinkTargetSelectorProps {
  value: string;
  onChange: (url: string, linkType: LinkType, linkedItem?: LinkOption) => void;
  error?: string;
}

const LINK_TYPES: { value: LinkType; label: string; icon: React.ReactNode; description: string }[] = [
  { value: 'category', label: 'Category', icon: <CategoryIcon />, description: 'Link to a product category' },
  { value: 'product', label: 'Product', icon: <InventoryIcon />, description: 'Link to a specific product' },
  { value: 'brand', label: 'Brand', icon: <LocalOfferIcon />, description: 'Link to a specific brand' },
  { value: 'brands-page', label: 'All Brands', icon: <LocalOfferIcon />, description: 'Link to brands listing page (shows all brands)' },
  { value: 'store', label: 'Store', icon: <StorefrontIcon />, description: 'Link to a store page' },
  { value: 'custom', label: 'Custom URL', icon: <LinkIcon />, description: 'Enter any URL manually' },
];

/**
 * LinkTargetSelector Component
 *
 * A user-friendly component for selecting banner link destinations.
 * Supports linking to:
 * - Categories (with autocomplete search)
 * - Products (with autocomplete search)
 * - Brands (with autocomplete search)
 * - Stores (with autocomplete search)
 * - Custom URLs (manual entry)
 *
 * The component automatically generates appropriate URLs based on selection
 * and parses existing URLs to restore the selected state.
 */
const LinkTargetSelector: React.FC<LinkTargetSelectorProps> = ({
  value,
  onChange,
  error,
}) => {
  const theme = useTheme();
  const [linkType, setLinkType] = useState<LinkType>('custom');
  const [options, setOptions] = useState<LinkOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<LinkOption | null>(null);
  const [customUrl, setCustomUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [initialized, setInitialized] = useState(false);

  // Pagination state for infinite scroll (products only)
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const isFetchingRef = useRef(false);

  // Page size for product infinite scroll
  const PRODUCT_PAGE_SIZE = 50;

  // Parse existing URL to determine link type and fetch corresponding item
  useEffect(() => {
    if (!value || initialized) return;

    const parseUrl = async () => {
      // Try to detect link type from URL
      if (value.includes('/category/') || value.includes('/categories/')) {
        const id = extractIdFromUrl(value, ['category', 'categories']);
        if (id) {
          setLinkType('category');
          await fetchAndSetItem('categories', id);
        }
      } else if (value.includes('/product/') || value.includes('/products/')) {
        const id = extractIdFromUrl(value, ['product', 'products']);
        if (id) {
          setLinkType('product');
          await fetchAndSetItem('products', id);
        }
      } else if (value === '/brands' || value === 'https://www.deeliver.co.za/brands') {
        // Brands listing page (no ID)
        setLinkType('brands-page');
      } else if (value.includes('/brand/') || value.includes('/brands/')) {
        const id = extractIdFromUrl(value, ['brand', 'brands']);
        if (id) {
          setLinkType('brand');
          await fetchAndSetItem('brands', id);
        }
      } else if (value.includes('/store/') || value.includes('/stores/')) {
        const id = extractIdFromUrl(value, ['store', 'stores']);
        if (id) {
          setLinkType('store');
          await fetchAndSetItem('stores', id);
        }
      } else {
        setLinkType('custom');
        setCustomUrl(value);
      }
      setInitialized(true);
    };

    parseUrl();
  }, [value, initialized]);

  const extractIdFromUrl = (url: string, segments: string[]): string | null => {
    for (const segment of segments) {
      const regex = new RegExp(`/${segment}/([^/]+)`);
      const match = url.match(regex);
      if (match) return match[1];
    }
    return null;
  };

  const fetchAndSetItem = async (endpoint: string, id: string) => {
    try {
      const { data } = await authInstance.get(`/${endpoint}/${id}`);
      setSelectedOption({ id: data.id, name: data.name, imageUrl: data.imageUrl });
    } catch (err) {
      console.error('Error fetching linked item:', err);
    }
  };

  const getApiEndpoint = (type: LinkType): string => {
    switch (type) {
      case 'category': return 'categories';
      case 'product': return 'products';
      case 'brand': return 'brands';
      case 'store': return 'stores';
      default: return '';
    }
  };

  const fetchOptions = useCallback(async (
    type: LinkType,
    query: string = '',
    pageNum: number = 0,
    append: boolean = false
  ) => {
    if (type === 'custom' || type === 'brands-page') return;
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    const isFirstPage = pageNum === 0;

    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const endpoint = getApiEndpoint(type);
      const isProductSearch = type === 'product';

      // Products use dedicated /search endpoint with pagination for infinite scroll
      // Other types fetch all at once (limited count)
      const searchEndpoint = isProductSearch ? `${endpoint}/search` : endpoint;
      const limit = isProductSearch ? PRODUCT_PAGE_SIZE : 50;
      // Backend uses 1-based page numbering
      const apiPage = isProductSearch ? pageNum + 1 : 1;

      let queryParams = `?limit=${limit}`;
      if (isProductSearch) {
        queryParams += `&page=${apiPage}`;
      }
      if (query) {
        queryParams += `&q=${encodeURIComponent(query)}`;
      }

      const { data } = await authInstance.get(`/${searchEndpoint}${queryParams}`);

      const items = data.data || data || [];
      const total = data.total || items.length;

      const mappedItems = items.map((item: any) => ({
        id: item.id,
        name: item.name || item.title,
        imageUrl: item.imageUrl || item.image,
      }));

      if (append && isProductSearch) {
        // Append to existing options (infinite scroll)
        setOptions(prev => {
          // Dedupe by id to prevent duplicates
          const existingIds = new Set(prev.map(p => p.id));
          const newItems = mappedItems.filter((item: LinkOption) => !existingIds.has(item.id));
          return [...prev, ...newItems];
        });
      } else {
        setOptions(mappedItems);
      }

      // Update pagination state for products
      if (isProductSearch) {
        setTotalCount(total);
        const loadedCount = (pageNum + 1) * PRODUCT_PAGE_SIZE;
        setHasMore(loadedCount < total);
        setPage(pageNum);
      } else {
        setHasMore(false);
      }
    } catch (err: any) {
      console.error('Error fetching options:', err);
      if (!append) {
        setOptions([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, []);

  // Fetch options when link type changes
  useEffect(() => {
    if (linkType !== 'custom' && linkType !== 'brands-page' && initialized) {
      setOptions([]);
      setSelectedOption(null);
      setPage(0);
      setHasMore(true);
      setTotalCount(0);
      fetchOptions(linkType, '', 0, false);
    }
  }, [linkType, fetchOptions, initialized]);

  // Debounced search - reset pagination on new search
  useEffect(() => {
    if (linkType === 'custom' || linkType === 'brands-page') return;

    const timer = setTimeout(() => {
      setPage(0);
      setHasMore(true);
      setTotalCount(0);
      fetchOptions(linkType, searchQuery, 0, false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, linkType, fetchOptions]);

  // Load more products when scrolling near the bottom (prefetch at 70% scroll)
  const handleListboxScroll = useCallback((event: React.UIEvent<HTMLUListElement>) => {
    if (linkType !== 'product' || !hasMore || loadingMore || isFetchingRef.current) return;

    const listbox = event.currentTarget;
    const scrollTop = listbox.scrollTop;
    const scrollHeight = listbox.scrollHeight;
    const clientHeight = listbox.clientHeight;

    // Prefetch when user has scrolled 70% of the way
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    if (scrollPercentage > 0.7) {
      fetchOptions(linkType, searchQuery, page + 1, true);
    }
  }, [linkType, hasMore, loadingMore, page, searchQuery, fetchOptions]);

  const handleLinkTypeChange = (newType: LinkType) => {
    setLinkType(newType);
    setSelectedOption(null);
    setCustomUrl('');
    setSearchQuery('');
    // Reset pagination state
    setPage(0);
    setHasMore(true);
    setTotalCount(0);
    setOptions([]);

    if (newType === 'custom') {
      onChange('', 'custom');
    } else if (newType === 'brands-page') {
      // Automatically set URL for brands listing page
      onChange('https://www.deeliver.co.za/brands', 'brands-page');
    }
  };

  const handleOptionSelect = (option: LinkOption | null) => {
    setSelectedOption(option);
    if (option) {
      const url = `https://www.deeliver.co.za/${linkType}/${option.id}`;
      onChange(url, linkType, option);
    } else {
      onChange('', linkType);
    }
  };

  const handleCustomUrlChange = (url: string) => {
    setCustomUrl(url);
    onChange(url, 'custom');
  };

  const getLinkTypeInfo = (type: LinkType) => LINK_TYPES.find(t => t.value === type);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        Banner Link Target
      </Typography>

      {/* Link Type Selector - Visual Cards */}
      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        {LINK_TYPES.map((type) => (
          <Paper
            key={type.value}
            onClick={() => handleLinkTypeChange(type.value)}
            sx={{
              p: 1.5,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: '2px solid',
              borderColor: linkType === type.value
                ? theme.palette.primary.main
                : alpha(theme.palette.divider, 0.5),
              borderRadius: 2,
              transition: 'all 0.2s ease',
              bgcolor: linkType === type.value
                ? alpha(theme.palette.primary.main, 0.08)
                : 'background.paper',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                transform: 'translateY(-2px)',
                boxShadow: 2,
              },
            }}
          >
            <Box sx={{
              color: linkType === type.value
                ? theme.palette.primary.main
                : theme.palette.text.secondary
            }}>
              {type.icon}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: linkType === type.value ? 600 : 400,
                color: linkType === type.value
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
              }}
            >
              {type.label}
            </Typography>
          </Paper>
        ))}
      </Stack>

      {/* Description */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {getLinkTypeInfo(linkType)?.description}
      </Typography>

      {/* Link Target Input */}
      {linkType === 'custom' ? (
        <TextField
          fullWidth
          label="Custom URL"
          value={customUrl}
          onChange={(e) => handleCustomUrlChange(e.target.value)}
          placeholder="https://example.com or /products/123"
          helperText="Enter a full URL (https://...) or a relative path (/products/123)"
          error={!!error}
          InputProps={{
            startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
      ) : linkType === 'brands-page' ? (
        <Alert severity="success" icon={<LocalOfferIcon />}>
          <Typography variant="body2">
            <strong>All set!</strong> This banner will link to the brands listing page, showing all available brands to customers.
          </Typography>
        </Alert>
      ) : (
        <Autocomplete
          options={options}
          value={selectedOption}
          onChange={(_, newValue) => handleOptionSelect(newValue)}
          onInputChange={(_, newValue) => setSearchQuery(newValue)}
          getOptionLabel={(option) => option.name}
          loading={loading}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          ListboxProps={{
            onScroll: handleListboxScroll,
            style: { maxHeight: 300 },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`Select ${getLinkTypeInfo(linkType)?.label}`}
              placeholder={`Search ${linkType}s...`}
              error={!!error}
              helperText={
                error ||
                (linkType === 'product' && totalCount > 0
                  ? `Showing ${options.length} of ${totalCount} products. Scroll for more.`
                  : `Type to search for a ${linkType}`)
              }
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {getLinkTypeInfo(linkType)?.icon}
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {(loading || loadingMore) ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const index = options.findIndex(o => o.id === option.id);
            const isLastItem = index === options.length - 1;
            return (
              <React.Fragment key={option.id}>
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {option.imageUrl && (
                    <Box
                      component="img"
                      src={option.imageUrl}
                      alt={option.name}
                      sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }}
                    />
                  )}
                  <Typography>{option.name}</Typography>
                </Box>
                {/* Show loading indicator at the bottom when loading more */}
                {isLastItem && loadingMore && linkType === 'product' && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
                {/* Show "Load more" hint at the bottom when there's more */}
                {isLastItem && !loadingMore && hasMore && linkType === 'product' && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'center',
                      py: 1,
                      color: 'text.secondary',
                    }}
                  >
                    Scroll for more products...
                  </Typography>
                )}
              </React.Fragment>
            );
          }}
          noOptionsText={loading ? 'Loading...' : 'No results found'}
        />
      )}

      {/* Selected Link Preview */}
      {(selectedOption || customUrl || linkType === 'brands-page') && (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
          icon={getLinkTypeInfo(linkType)?.icon}
        >
          <Typography variant="body2" component="div">
            <strong>Link Target:</strong>{' '}
            {selectedOption ? (
              <>
                {selectedOption.name}
                <Chip
                  label={linkType}
                  size="small"
                  sx={{ ml: 1 }}
                  color="primary"
                  variant="outlined"
                />
              </>
            ) : linkType === 'brands-page' ? (
              <>
                All Brands Page
                <Chip
                  label="brands-page"
                  size="small"
                  sx={{ ml: 1 }}
                  color="primary"
                  variant="outlined"
                />
              </>
            ) : (
              customUrl
            )}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default LinkTargetSelector;
